import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Radar,
  Search,
  type LucideIcon,
} from "lucide-react";

import { AIVisibilityMap, LiveMarketSignals } from "./product-modules";

/* ────────────── Outcome band ────────────── */
type Outcome = {
  value: number;
  prefix?: string;
  suffix: string;
  label: string;
  proof: string;
};

const outcomes: Outcome[] = [
  {
    value: 11,
    suffix: "×",
    label: "AI citations & mentions",
    proof: "VS. PRE-AXY.DIGITAL BASELINE",
  },
  {
    value: 40,
    prefix: "+",
    suffix: "%",
    label: "organic traffic",
    proof: "90-day median",
  },
  {
    value: 5,
    suffix: "×",
    label: "social engagement",
    proof: "founder accounts",
  },
  {
    value: 10000,
    suffix: "+",
    label: "signals analysed / week",
    proof: "per customer",
  },
];

function useInView<T extends HTMLElement>(threshold = 0.3) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setSeen(true);
            io.disconnect();
          }
        });
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, seen };
}

function Counter({
  to,
  prefix = "",
  suffix = "",
}: {
  to: number;
  prefix?: string;
  suffix?: string;
}) {
  const { ref, seen } = useInView<HTMLSpanElement>(0.4);
  // Start at the real value so SSR / no-JS / AI crawlers read the true number,
  // not "0". The count-up animation runs on top of it, client-side only.
  const [n, setN] = useState(to);
  useEffect(() => {
    if (!seen) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setN(to);
      return;
    }
    const start = performance.now();
    const dur = 1600;
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setN(to * eased);
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, to]);
  const display =
    to >= 1000
      ? `${Math.floor(n).toLocaleString()}`
      : Number.isInteger(to)
        ? `${Math.round(n)}`
        : n.toFixed(1);
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export function OutcomeMetricsBar({ className = "" }: { className?: string }) {
  return (
    <div className={`glass-card p-6 md:p-8 ${className}`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-4 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
        {outcomes.map((o, i) => (
          <div key={o.label} className={`px-2 lg:px-6 ${i >= 2 ? "pt-8 lg:pt-0" : ""}`}>
            <div className="text-4xl md:text-5xl font-semibold leading-none text-gradient">
              <Counter to={o.value} prefix={o.prefix} suffix={o.suffix} />
            </div>
            <p className="mt-3 text-sm font-medium text-white/90">{o.label}</p>
            <p className="mt-1 text-[11px] uppercase tracking-wider text-white/45">{o.proof}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────── Pillar tabs ────────────── */
type PillarStat = {
  primary: string;
  delta?: string;
  beforeAfter?: string;
  proof: string;
};

type Pillar = {
  id: string;
  slug: string;
  eyebrow: string;
  tabLabel: string;
  icon: LucideIcon;
  title: ReactNode;
  body: string;
  stats: PillarStat[];
  visual: ReactNode;
};

const pillars: Pillar[] = [
  {
    id: "geo",
    slug: "geo-aeo-seo-autopilot",
    eyebrow: "GEO · AEO · SEO",
    tabLabel: "GEO / AEO / SEO",
    icon: Search,
    title: (
      <>
        Get cited where buyers <span className="text-gradient">actually ask</span>
      </>
    ),
    body: "Axy.digital maps prompts based on real search trends and optimises your brand to be cited in Google AI overviews, Gemini, ChatGPT, Claude and Perplexity.",
    stats: [
      {
        primary: "11×",
        delta: "↑ AI visibility",
        beforeAfter: "30 → 330 mentions & citations / mo",
        proof: "90-DAY AVERAGE",
      },
      {
        primary: "+40%",
        delta: "↑ organic traffic",
        beforeAfter: "12k → 17k visits / mo",
        proof: "median across customers",
      },
    ],
    visual: <AIVisibilityMap />,
  },
  {
    id: "intelligence",
    slug: "market-intelligence",
    eyebrow: "Market Intelligence",
    tabLabel: "Intelligence",
    icon: Radar,
    title: (
      <>
        A&nbsp;live view of your industry
      </>
    ),
    body: "A living dashboard of what your customers are searching for and engaging with, synthesised into the trends that matter most this week.",
    stats: [
      {
        primary: "10,000+",
        delta: "data analysed",
        beforeAfter: "From search, social, competitors & more",
        proof: "DATA POINTS FROM YOUR MARKET",
      },
      {
        primary: "5 - 10",
        delta: "actionable signals",
        beforeAfter: "Signals & trends tracked weekly",
        proof: "SIGNALS INFORM GEO CONTENT",
      },
    ],
    visual: <LiveMarketSignals />,
  },
  {
    id: "analytics",
    slug: "ai-visibility-analytics",
    eyebrow: "Analytics & Learning",
    tabLabel: "Analytics",
    icon: BarChart3,
    title: (
      <>
        Cross-model visibility, <span className="text-gradient">all in one place</span>
      </>
    ),
    body: "Axy.digital tracks mentions and citations across every AI engine, benchmarks you against competitors, shows which of your own pages get cited most, and flags the third-party sites, publications, and Reddit threads worth targeting next.",
    stats: [
      {
        primary: "100+",
        delta: "prompts tracked",
        beforeAfter: "With detailed breakdown of LLM answers",
        proof: "WEEKLY",
      },
      {
        primary: "4",
        delta: "LLMs tracked",
        beforeAfter: "ChatGPT, Claude, Gemini, Perplexity",
        proof: "WEEKLY",
      },
    ],
    visual: <AnalyticsModule />,
  },
];

function StatCard({ stat }: { stat: PillarStat }) {
  return (
    <div className="glass-card px-4 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-2xl md:text-3xl font-semibold text-gradient tabular-nums leading-none">
          {stat.primary}
        </span>
        {stat.delta && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-pink-soft">
            <ArrowUpRight className="size-3" />
            {stat.delta.replace(/^[↑↓]\s?/, "")}
          </span>
        )}
      </div>
      {stat.beforeAfter && (
        <p className="mt-2 text-xs text-white/75 tabular-nums">{stat.beforeAfter}</p>
      )}
      <p className="mt-1 text-[10px] uppercase tracking-wider text-white/45">{stat.proof}</p>
    </div>
  );
}

function PillarTabs() {
  const [active, setActive] = useState(2);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % pillars.length);
    }, 6000);
    return () => clearInterval(id);
  }, [paused]);

  const p = pillars[active];

  return (
    <div onMouseEnter={() => setPaused(true)}>
      {/* Tabs */}
      <div className="relative -mx-6 px-6 lg:mx-0 lg:px-0 mb-6">
        <div
          role="tablist"
          aria-label="Inside Axy.digital pillars"
          className="flex gap-2 overflow-x-auto scrollbar-none snap-x snap-mandatory lg:flex-wrap lg:overflow-visible"
        >
          {pillars.map((pillar, i) => {
            const isActive = i === active;
            const Icon = pillar.icon;
            return (
              <button
                key={pillar.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setPaused(true);
                  setActive(i);
                }}
                className={`snap-start shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 ${
                  isActive
                    ? "bg-gradient-to-r from-pink-soft/30 to-violet/30 border-white/30 text-white shadow-[0_10px_30px_rgba(217,70,239,0.35)]"
                    : "bg-white/[0.04] border-white/10 text-white/65 hover:text-white hover:border-white/20"
                }`}
              >
                <Icon className="size-4" />
                <span className="whitespace-nowrap">{pillar.tabLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Panel */}
      <div key={p.id} className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center animate-fade-in">
        <div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-pink-soft font-medium">
            {p.eyebrow}
          </span>
          <h3 className="mt-3 text-2xl md:text-4xl font-semibold tracking-tight text-white text-balance leading-tight">
            {p.title}
          </h3>
          <p className="mt-4 text-base text-white/70 leading-relaxed max-w-xl">{p.body}</p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
            {p.stats.map((s) => (
              <StatCard key={s.primary + s.proof} stat={s} />
            ))}
          </div>
          <Link
            to="/products/$slug"
            params={{ slug: p.slug }}
            className="mt-7 inline-flex items-center gap-1 text-sm font-medium text-pink-soft hover:gap-2 transition-all"
          >
            Explore {p.eyebrow} <ArrowRight className="size-4" />
          </Link>
        </div>
        <div>{p.visual}</div>
      </div>
    </div>
  );
}

/* ────────────── Analytics module (used inside Analytics tab) ────────────── */
function AnalyticsModule() {
  const channels = [
    { name: "AI visibility", value: 78, delta: "+34%" },
    { name: "LinkedIn", value: 64, delta: "+28%" },
    { name: "X", value: 52, delta: "+41%" },
    { name: "Organic search", value: 71, delta: "+19%" },
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
            CROSS-MODEL ANALYTICS
          </p>
          <h3 className="text-lg font-semibold text-white">One dashboard. Every channel.</h3>
        </div>
        <span className="glass-pill px-2.5 py-1 text-[10px] uppercase tracking-wider text-white/80 inline-flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />
          Live
        </span>
      </div>
      <div className="space-y-4">
        {channels.map((c) => (
          <div key={c.name}>
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <span className="font-medium text-white/85">{c.name}</span>
              <span className="text-pink-soft tabular-nums">{c.delta}</span>
            </div>
            <div className="h-2 rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-pink-soft to-violet"
                style={{ width: `${c.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {[
          { k: "Reach", v: "1.2M" },
          { k: "Citations", v: "284" },
          { k: "Pipeline", v: "$840k" },
        ].map((s) => (
          <div
            key={s.k}
            className="rounded-xl px-3 py-3 bg-white/[0.04] border border-white/10 text-center"
          >
            <p className="text-base font-semibold text-white tabular-nums">{s.v}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{s.k}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────── Composed export ────────────── */
export function InsideAxy() {
  return <PillarTabs />;
}
